import React, { useState } from 'react';
import { ArrowLeft, ChevronRight, Folder, FolderPlus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { getWorkflowName, renderText } from '../../utils/workflowLibraryUtils';

const WorkflowMoveToFolderModal = ({
  isOpen,
  onClose,
  workflow,
  folders = [],
  onMove,
  onCreateFolder,
  moving = false,
}) => {
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [browseFolderId, setBrowseFolderId] = useState(null);

  const browseFolder = browseFolderId
    ? folders.find((folder) => folder.id === browseFolderId)
    : null;

  const visibleFolders = browseFolderId
    ? folders.filter((folder) => folder.parent_id === browseFolderId)
    : folders.filter((folder) => !folder.parent_id);

  const handleOpenChange = (open) => {
    if (!open) {
      setSelectedFolderId(null);
      setBrowseFolderId(null);
      onClose?.();
    }
  };

  const handleMove = () => {
    const targetId = selectedFolderId || browseFolderId;
    if (!targetId) return;
    onMove?.(targetId);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-md bg-zinc-900 border-zinc-700 text-white"
        data-testid="workflow-move-to-folder-modal"
      >
        <DialogHeader>
          <DialogTitle>Move 1 item to folder</DialogTitle>
          <DialogDescription className="text-zinc-400">
            Pick a folder to move this item into.
          </DialogDescription>
        </DialogHeader>

        {browseFolder ? (
          <button
            type="button"
            className="mb-2 flex items-center gap-2 text-sm text-zinc-400 hover:text-white"
            onClick={() => {
              setBrowseFolderId(null);
              setSelectedFolderId(null);
            }}
          >
            <ArrowLeft size={14} />
            Back
          </button>
        ) : null}

        <div className="max-h-64 space-y-1 overflow-y-auto rounded-lg border border-zinc-800 p-2">
          {visibleFolders.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-zinc-500">
              {browseFolder
                ? 'Move here or create a new folder'
                : 'No folders yet. Create one to organize workflows.'}
            </div>
          ) : (
            visibleFolders.map((folder) => (
              <button
                key={folder.id}
                type="button"
                data-testid={`move-folder-option-${folder.id}`}
                onClick={() => setSelectedFolderId(folder.id)}
                className={cn(
                  'flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm transition-colors',
                  selectedFolderId === folder.id
                    ? 'bg-emerald-500/15 text-emerald-300'
                    : 'text-zinc-300 hover:bg-zinc-800',
                )}
              >
                <span className="flex items-center gap-2">
                  <Folder size={15} />
                  {renderText(folder.name)}
                </span>
                <ChevronRight size={14} className="text-zinc-500" />
              </button>
            ))
          )}
        </div>

        {workflow?.name ? (
          <p className="text-xs text-zinc-500">Moving: {getWorkflowName(workflow)}</p>
        ) : null}

        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            type="button"
            variant="outline"
            className="border-zinc-700 text-zinc-300"
            onClick={() => onCreateFolder?.()}
          >
            <FolderPlus size={14} className="mr-1.5" />
            New folder
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" className="border-zinc-700 text-zinc-300" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="button"
              data-testid="workflow-move-here-button"
              disabled={moving || !(selectedFolderId || browseFolderId)}
              className="bg-blue-600 hover:bg-blue-700"
              onClick={handleMove}
            >
              {moving ? 'Moving...' : 'Move Here'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WorkflowMoveToFolderModal;
